import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);

    if (this.#isClickTrigger) {
      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('keydown', this.#onDocumentKeydown);
    }

    document.addEventListener('click', this.#onFullScreenOverlayClick);
    document.addEventListener('keydown', this.#onFullScreenOverlayKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointermove', this.#onHoverPointerMove, true);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('click', this.#onFullScreenOverlayClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    document.removeEventListener('keydown', this.#onFullScreenOverlayKeydown);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#fadeCloseTimer);
    this.#fadeCloseTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();

    if (this.#state.activeItem) {
      const submenu = findSubmenu(this.#state.activeItem);

      if (submenu) {
        this.#applyOpenHeights(this.#measureSubmenuHeight(submenu));
      }
    }
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Whether megamenus open on click instead of hover.
   * @returns {boolean}
   */
  get #isClickTrigger() {
    return this.dataset.openTrigger === 'click';
  }

  /**
   * Measure the real submenu content height, avoiding contain-intrinsic-size placeholders.
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu) {
    const inner = /** @type {HTMLElement | null} */ (submenu.querySelector('.menu-list__submenu-inner'));
    const innerHeight = inner?.scrollHeight || 0;
    if (innerHeight > 0) return innerHeight;

    const offsetHeight = submenu.offsetHeight || 0;
    // Theme sets contain-intrinsic-size: 0px 500px — ignore that placeholder
    if (offsetHeight === 500) return 0;
    return offsetHeight;
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #fadeCloseTimer;

  /**
   * @returns {boolean}
   */
  get #isFadeTransition() {
    return this.dataset.overlayTransition === 'fade';
  }

  /**
   * @returns {number}
   */
  #getSubmenuAnimationSpeed() {
    const source = this.headerComponent ?? this;
    const speed = getComputedStyle(source).getPropertyValue('--submenu-animation-speed').trim();

    if (speed.endsWith('ms')) return Number.parseFloat(speed);
    if (speed.endsWith('s')) return Number.parseFloat(speed) * 1000;
    return 360;
  }

  /**
   * Total close duration for staged fade (content out, then background).
   * @returns {number}
   */
  #getFadeCloseDuration() {
    return Math.round(this.#getSubmenuAnimationSpeed() * 1.4);
  }

  /**
   * @returns {boolean}
   */
  get #isFullScreenOverlay() {
    return this.dataset.overlaySize === 'full_screen';
  }

  /**
   * Submenu panel height when full-screen overlay is enabled.
   * @returns {number}
   */
  #getFullScreenSubmenuHeight() {
    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    return Math.max(0, window.innerHeight - headerVisibleHeight);
  }

  /**
   * Apply open header height vars together so top/transform never see a half-updated state.
   * @param {number} submenuHeight
   */
  #applyOpenHeights(submenuHeight) {
    if (!this.headerComponent) return;

    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    const isFullScreenOpen = this.#isFullScreenOverlay && this.ariaExpanded === 'true';
    const effectiveSubmenuHeight = isFullScreenOpen
      ? this.#getFullScreenSubmenuHeight()
      : submenuHeight;
    const fullOpenHeaderHeight =
      effectiveSubmenuHeight > 0 ? effectiveSubmenuHeight + headerVisibleHeight : 0;

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
    this.headerComponent.style.setProperty('--submenu-height', `${effectiveSubmenuHeight}px`);
  }

  /**
   * Document-level hover tracker: keep open only over the active trigger or its megamenu.
   * Close when the pointer moves to another top-level link or leaves the menu entirely.
   * @param {PointerEvent} event
   */
  #onHoverPointerMove = (event) => {
    if (this.#isClickTrigger || !this.#state.activeItem) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const activeLink = this.#state.activeItem;
    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = setTimeout(() => {
      if (!this.#state.activeItem) return;
      this.#state.activeItem.dataset.safetyBox = 'false';
      this.#reconcileHoverTarget(this.#lastPointer.x, this.#lastPointer.y);
    }, 40);

    // Reconcile immediately while moving so sibling/outside closes feel snappy
    this.#reconcileHoverTarget(event.clientX, event.clientY);
  };

  /**
   * Decide whether to keep, switch, or close the open megamenu based on the pointer position.
   * @param {number} x
   * @param {number} y
   */
  #reconcileHoverTarget(x, y) {
    if (this.#isClickTrigger || !this.#state.activeItem) return;

    const target = document.elementFromPoint(x, y);
    if (!target) {
      this.#deactivate();
      return;
    }

    const activeListItem = this.#state.activeItem.closest('.menu-list__list-item');
    const activeSubmenu = findSubmenu(this.#state.activeItem);
    const hitTopLevelLink = target.closest('.menu-list__link');

    // Full-screen overlay: keep the menu open while hovering header logo and nav links
    if (this.#isFullScreenOverlay && this.#isFullScreenHeaderChromeTarget(target)) {
      return;
    }

    // Pointer is over a different top-level nav link — close (or switch if it has a submenu)
    if (hitTopLevelLink && !this.#state.activeItem.contains(hitTopLevelLink) && hitTopLevelLink !== this.#state.activeItem) {
      const nextListItem = hitTopLevelLink.closest('.menu-list__list-item');
      const nextItem = nextListItem ? findMenuItem(nextListItem) : null;
      if (nextItem && findSubmenu(nextItem)) {
        nextListItem?.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      } else {
        this.#deactivate();
      }
      return;
    }

    // Still over the open megamenu panel or its trigger
    if (activeSubmenu?.contains(target)) return;
    if (activeListItem?.contains(target)) return;

    // Open underlay is the megamenu chrome — keep open while over it
    if (target.closest('.header__underlay-open')) return;

    // Pointer is outside the active trigger and megamenu
    this.#deactivate();
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (this.#isClickTrigger) return;

    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.addEventListener('pointermove', this.#onHoverPointerMove, true);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Whether the pointer target is header chrome that should stay clickable over a full-screen overlay.
   * @param {Element} target
   * @returns {boolean}
   */
  #isFullScreenHeaderChromeTarget(target) {
    return !!target.closest('.header-logo, .header__columns .menu-list__link, .header__columns .header-actions__action');
  }

  /**
   * Whether the event target is an interactive element inside the open megamenu.
   * @param {Element} target
   * @returns {boolean}
   */
  #isSubmenuInteractiveTarget(target) {
    return !!target.closest(
      '.mega-menu__link, .mega-menu__content a, product-component, .resource-card, button, input, select, textarea, [role="button"]'
    );
  }

  /**
   * Close the full-screen megamenu overlay when clicking outside interactive submenu content.
   * @param {MouseEvent} event
   */
  #onFullScreenOverlayClick = (event) => {
    if (!this.#isFullScreenOverlay || !this.#state.activeItem) return;
    if (!(event.target instanceof Element)) return;

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (!activeSubmenu) return;

    const inOverlay =
      activeSubmenu.contains(event.target) || !!event.target.closest('.header__underlay-open');
    if (!inOverlay) return;

    if (this.#isFullScreenHeaderChromeTarget(event.target)) {
      this.#deactivate();
      return;
    }

    if (this.#isSubmenuInteractiveTarget(event.target)) return;

    const topLevelLink = event.target.closest('.menu-list__link');
    if (topLevelLink?.hasAttribute('aria-haspopup')) return;

    event.preventDefault();
    this.#deactivate();
  };

  /**
   * Close the full-screen megamenu overlay on Escape.
   * @param {KeyboardEvent} event
   */
  #onFullScreenOverlayKeydown = (event) => {
    if (!this.#isFullScreenOverlay || !this.#state.activeItem || event.key !== 'Escape') return;
    this.#deactivate();
  };

  /**
   * Close the open megamenu when clicking outside it (click-to-open mode).
   * Uses bubble phase so the opening click is handled by toggle first.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#isClickTrigger || !this.#state.activeItem) return;
    if (!(event.target instanceof Element)) return;

    // Top-level triggers with submenus are toggled by toggle(), not this handler
    if (event.target.closest('.menu-list__link[aria-haspopup]')) return;

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (activeSubmenu?.contains(event.target)) return;

    this.#deactivate();
  };

  /**
   * Close the open megamenu on Escape (click-to-open mode).
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (!this.#isClickTrigger || event.key !== 'Escape') return;
    this.#deactivate();
  };

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle a megamenu open/closed on click (click-to-open mode).
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const submenu = findSubmenu(item);
    const clickTarget = /** @type {Element} */ (event.composedPath?.()[0] ?? event.target);

    // Clicks on megamenu panel links should navigate, not toggle the parent
    if (submenu?.contains(clickTarget)) return;

    // Links without a submenu should navigate normally
    if (!submenu) return;

    event.preventDefault();

    if (item.getAttribute('aria-expanded') === 'true') {
      this.#deactivate();
      return;
    }

    this.activate(event);
  };

  /**
   * While a megamenu is open (click mode), hovering another top-level link closes it.
   * @param {PointerEvent} event
   */
  onSiblingHover = (event) => {
    if (!this.#isClickTrigger || !this.#state.activeItem) return;
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    this.#deactivate();
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item || item.getAttribute('aria-expanded') === 'true') return;

    clearTimeout(this.#fadeCloseTimer);
    this.#fadeCloseTimer = undefined;

    const submenu = findSubmenu(item);

    // Hovering/focusing a top-level link without a submenu should close any open megamenu
    if (!submenu) {
      this.#deactivate();
      return;
    }

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveSubmenu = findSubmenu(previouslyActiveItem);

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveSubmenu && previouslyActiveSubmenu !== submenu) {
      delete previouslyActiveSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    const committedItem = item;
    if (event instanceof FocusEvent || this.#isClickTrigger) {
      this.dispatchEvent(new MegaMenuHoverEvent());
    } else {
      this.#hoverDispatchTimer = setTimeout(() => {
        this.#hoverDispatchTimer = undefined;
        if (this.#state.activeItem === committedItem) {
          this.dispatchEvent(new MegaMenuHoverEvent());
        }
      }, HOVER_COMMIT_DELAY_MS);
    }

    // Mark submenu as active for content-visibility optimization BEFORE measuring
    submenu.dataset.active = '';

    // Cleanup any existing mutation observer from previous menu activations
    this.#cleanupMutationObserver();

    // Monitor DOM mutations to catch deferred content injection (from section hydration)
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.#state.activeItem || this.#state.activeItem !== committedItem) return;
          const nextHeight = this.#measureSubmenuHeight(submenu);
          if (nextHeight > 0) {
            this.#applyOpenHeights(nextHeight);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

    // Auto-disconnect after 500ms to prevent memory leaks
    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);

    this.#applyOpenHeights(this.#measureSubmenuHeight(submenu));
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after leaving the trigger
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    if (this.#isClickTrigger && event.type === 'pointerleave') return;

    const menu = findSubmenu(this.#state.activeItem);

    // Moving into the open megamenu keeps it open
    if (event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget)) {
      return;
    }

    // Moving to another top-level menu item: let hover tracker / activate handle it
    if (event.relatedTarget instanceof Element) {
      const nextListItem = event.relatedTarget.closest('.menu-list__list-item');
      if (nextListItem) {
        const nextItem = findMenuItem(nextListItem);
        if (nextItem && nextItem !== this.#state.activeItem) {
          if (!findSubmenu(nextItem)) {
            this.#deactivate();
          }
          return;
        }
      }
    }

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);

    if (isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    const submenu = findSubmenu(item);

    document.removeEventListener('pointermove', this.#onHoverPointerMove, true);
    this.#stopPointerTracking(item);

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    const finishDeactivate = () => {
      this.#applyOpenHeights(0);

      if (submenu) {
        delete submenu.dataset.active;
      }
    };

    if (this.#isFadeTransition) {
      clearTimeout(this.#fadeCloseTimer);
      this.#state.activeItem = null;
      this.#fadeCloseTimer = setTimeout(() => {
        this.#fadeCloseTimer = undefined;
        finishDeactivate();
      }, this.#getFadeCloseDuration());
      return;
    }

    this.#state.activeItem = null;
    finishDeactivate();
  };

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const topRow = /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'));
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    if (isOverlapSituation && topRow?.offsetHeight) return topRow.offsetHeight;
    if (this.headerComponent.offsetHeight > 0) return this.headerComponent.offsetHeight;
    if (topRow?.offsetHeight) return topRow.offsetHeight;

    const headerHeightVar = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
    const parsed = Number.parseFloat(headerHeightVar);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) return /** @type {HTMLElement} */ (element);

  return /** @type {HTMLElement | null} */ (element.querySelector('[ref="menuitem"]'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
